import { EstadosParaGeracaoJornadaEnum } from "./folha.enum";
import moment from "moment";
import type { DataEmFormatoISO, EmpregadoId } from "@/tipos";
import { ehDiaDaFolhaCompleto } from "./linha.interface";
import type { DiaDaFolhaDados, JornadaDiaria } from "./linha.interface";

type DadosParaResolverDiaDeTrabalho = Pick<
  JornadaDiaria,
  'eh_dia_de_trabalho' | 'eh_feriado' | 'eh_ferias' | 'eh_afastamento'
>;

export const resolverSeEhDiaDeTrabalho = (jornadaDiaria: DadosParaResolverDiaDeTrabalho): boolean => {
  return jornadaDiaria.eh_dia_de_trabalho
    ?? (!jornadaDiaria.eh_feriado && !jornadaDiaria.eh_ferias && !jornadaDiaria.eh_afastamento)
}

export const gerarChaveLinhaRecalculo = (empregadoId: string | number | undefined, data: string) => {
  if (!empregadoId) return ''
  return `${empregadoId}=${data}`
}

export const linhaAguardandoGeracaoJornada = (nomeJornada: string | undefined): boolean => {
  return nomeJornada === EstadosParaGeracaoJornadaEnum.AGUARDE_ATUALIZACAO;
}

/**
 * A jornada do dia está marcada para ser regerada a partir do criador de jornadas.
 * Só o dia que tem cálculo carrega essa flag — o dia sem folha nunca tem jornada
 * para regerar, e é por isso que o type guard vem antes da leitura do campo.
 */
export const jornadaPrecisaSerRegerada = (dia: DiaDaFolhaDados): boolean => {
  return ehDiaDaFolhaCompleto(dia) && dia.jornada_precisa_ser_regerada === true
}

/**
 * O dia sem folha não tem jornada e o back pede que ela seja gerada. Só o dia sem
 * cálculo carrega essa flag — o dia completo já tem jornada.
 */
export const jornadaPrecisaSerGerada = (dia: DiaDaFolhaDados): boolean => {
  return !ehDiaDaFolhaCompleto(dia) && dia.jornada_precisa_ser_gerada === true
}

/**
 * Agrupa os dias por empregado no formato que as rotas de recálculo e de
 * regeração esperam: `{ "<empregado_id>": ["2026-02-12T00:00:00", ...] }`.
 */
export const agruparDiasPorEmpregado = (
  linhas: ReadonlyArray<{ empregado_id: EmpregadoId; dia: DataEmFormatoISO }>
): Record<EmpregadoId, DataEmFormatoISO[]> => {
  const diasPorEmpregado: Record<EmpregadoId, DataEmFormatoISO[]> = {}

  linhas.forEach(linha => {
    if (!diasPorEmpregado[linha.empregado_id]) {
      diasPorEmpregado[linha.empregado_id] = []
    }
    diasPorEmpregado[linha.empregado_id].push(linha.dia)
  })

  return diasPorEmpregado
}

export const transformarDetalhesDeApontamentos = (detalhesApontamentos: Record<string, { descricao: string; abreviacao: string }>) => {
  return Object.fromEntries(
    Object.entries(detalhesApontamentos).map(([key, value]) => [
      key,
      {
        hover: value.descricao,
        text: value.abreviacao
      }
    ])
  )
}

/**
 * Gera uma chave específica para mesclar dados de approvals
 * É necessário esse utilitário ao invés de gerarChaveLinhaRecalculo devido a
 * API de approvals retornar datas contendo jornadas/horas/fusos
 * ex: "2026-03-02T07:00:00"
 * Ao formatar a data para "YYYY-MM-DD", garantimos o match das chaves
 */
export const gerarChaveParaMesclarApprovals = (employeeId: string, data: string) => {
  if (!employeeId || !data) return null

  const dataFormatada = moment(data).format("YYYY-MM-DD")
  return `${employeeId}=${dataFormatada}`
}
